import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization, OrganizationTier } from '@dadohr/shared/domainTypes/org';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField
} from '@material-ui/core';
import { truncate } from 'lodash';
import { useMemo, useState } from 'react';
import { Form } from '../../../../../components/Form';
import { FormField } from '../../../../../components/Form/FormField';
import { FormRow } from '../../../../../components/Form/FormRow';
import { FormSubmitButton } from '../../../../../components/Form/FormSubmitButton';
import { required } from '../../../../../components/Form/validators';
import {
  ItemSorters,
  RowsRenderer,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import {
  TableSearch,
  TableToolbarSection
} from '../../../../../components/Table';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { CanvasBar } from '../../../../../layout/Canvas';
import { FlexContainer } from '../../../../../layout/Flex';
import { Section } from '../../../../../layout/Section';
import { useRoutes } from '../../../../../routes';
import { setDoc } from '../../../../../services/firebase';
import { formatDate, toMoment } from '../../../../../services/time';
import { toSearchTermRegexp } from '../../../../../services/user';
import { getNewOrgDoc, useAllOrgs } from '../../../../services/orgs';
import { OrgTierChip } from '../../components/OrgTierChip';

type NewOrgValues = {
  name: string;
  domain: string;
  tz: string;
};

const createNewOrg = async (vs: NewOrgValues) => {
  const org = getNewOrgDoc(vs);
  await setDoc(org);
  return org;
};

const CreateNewOrgDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { goTo } = useRoutes();
  const initialValues: NewOrgValues = {
    name: '',
    domain: '',
    tz: 'America/Los_Angeles'
  };

  const onSubmit = async (vs: NewOrgValues) => {
    const org = await createNewOrg(vs);
    onClose();
    goTo(`/orgs/${org.id}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <Form<NewOrgValues> initialValues={initialValues} onSubmit={onSubmit}>
        {({ submitting, invalid }) => (
          <>
            <DialogTitle>Create Sample Org</DialogTitle>
            <DialogContent>
              <FormRow>
                <FormField<NewOrgValues['name']>
                  name="name"
                  validate={required}
                >
                  {({ input }) => (
                    <TextField
                      label="Name"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                    />
                  )}
                </FormField>
              </FormRow>
              <FormRow>
                <FormField<NewOrgValues['domain']>
                  name="domain"
                  validate={required}
                >
                  {({ input }) => (
                    <TextField
                      label="Domain"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                    />
                  )}
                </FormField>
              </FormRow>
              <FormRow>
                <FormField<NewOrgValues['tz']> name="tz" validate={required}>
                  {({ input }) => (
                    <TextField
                      label="Timezone"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      variant="outlined"
                    />
                  )}
                </FormField>
              </FormRow>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <FormSubmitButton
                variant="contained"
                color="primary"
                submitting={submitting}
                submitComponent="Creating..."
                disabled={invalid}
              >
                Create
              </FormSubmitButton>
            </DialogActions>
          </>
        )}
      </Form>
    </Dialog>
  );
};

type ColumnName =
  | 'id'
  | 'tier'
  | 'name'
  | 'domain'
  | 'tz'
  | 'admins'
  | 'createdAt';

type D = Doc<IOrganization>;
type Column = IColumn<D, ColumnName>;

const COLUMNS: Column[] = [
  {
    key: 'id',
    head: () => 'ID',
    cell: (d) => <div>{truncate(d.id, { length: 12 })}</div>,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 0
  },
  {
    key: 'tier',
    head: () => 'Tier',
    cell: (d) => <OrgTierChip small tier={d.data.tier} />,
    sortable: false,
    defaultDirection: 'asc',
    width: 50,
    flexGrow: 0,
    align: 'left'
  },
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) => d.data.name,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'domain',
    head: () => 'Domain',
    cell: (d) => d.data.domain,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 100,
    flexGrow: 3
  },
  {
    key: 'tz',
    head: () => 'TZ',
    cell: (d) => d.data.tz,
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 100,
    flexGrow: 0.8
  },
  {
    key: 'admins',
    head: () => '# of Admins',
    cell: (d) => Object.keys(d.data.permissions).length,
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 100
  },
  {
    key: 'createdAt',
    head: () => 'Created at',
    cell: (d) => formatDate(toMoment(d.data.createdAt), 'YYYY/MM/DD'),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 90,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D> = {
  id: {
    key: 'id',
    items: { sort: (d) => d.id, dir: 'asc' }
  },
  name: {
    key: 'name',
    items: { sort: (d) => d.data.name, dir: 'asc' }
  },
  domain: {
    key: 'domain',
    items: { sort: (d) => d.data.domain, dir: 'asc' }
  },
  admins: {
    key: 'admins',
    items: { sort: (d) => Object.keys(d.data.permissions).length, dir: 'desc' }
  },

  tz: {
    key: 'tz',
    items: { sort: (d) => d.data.tz, dir: 'asc' }
  },

  createdAt: {
    key: 'createdAt',
    items: { sort: (d) => d.data.createdAt.toMillis(), dir: 'desc' }
  }
};

const DEFAULT_SORTER = SORTERS.name;

const ROW_TO_KEY = (d: D) => d.id;

export const PageOrgsOverview = () => {
  const [q, setQ] = useState('');
  const [ds, loading, error] = useAllOrgs();
  const [tiers, setTiers] = useState<OrganizationTier[]>([
    OrganizationTier.FREE,
    OrganizationTier.STANDARD
  ]);
  const toggleTier = (tier: OrganizationTier) =>
    setTiers((p) =>
      p.includes(tier) ? p.filter((x) => x !== tier) : [...p, tier]
    );
  useErrorLogger('allOrgs', error);
  const [[sorter, dir], setSort] = useSortQueryParam('sort', SORTERS);
  const {
    dialogOpen: newOrgDialog,
    openDialog: openNewOrgDialog,
    closeDialog: closeNewOrgDialog
  } = useDialogState();

  const filteredDs = useMemo(() => {
    if (!ds) {
      return null;
    }
    const tiered = ds.filter((d) => tiers.includes(d.data.tier));
    if (!q) {
      return tiered;
    }
    const regexp = toSearchTermRegexp(q);
    return tiered.filter(
      (d) => d.id.match(regexp) || d.data.name.match(regexp)
    );
  }, [ds, tiers, q]);
  return (
    <Section>
      <CanvasBar>
        <FlexContainer>
          <TableSearch
            term={q}
            onChange={setQ}
            placeholder="Search by ID or name"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={tiers.includes(OrganizationTier.FREE)}
              />
            }
            onClick={() => toggleTier(OrganizationTier.FREE)}
            label="Free Tier"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={tiers.includes(OrganizationTier.STANDARD)}
              />
            }
            onClick={() => toggleTier(OrganizationTier.STANDARD)}
            label="Standard Tier"
          />
        </FlexContainer>
        <TableToolbarSection>
          <Button
            variant="contained"
            color="primary"
            onClick={openNewOrgDialog}
          >
            Create new org...
          </Button>
        </TableToolbarSection>
      </CanvasBar>
      {loading && <Loader height={500} />}
      {filteredDs && (
        <RowsRenderer
          variant="contained"
          columns={COLUMNS}
          rows={filteredDs}
          rowToKey={ROW_TO_KEY}
          rowToHref={(d) => `/orgs/${d.id}`}
          sorter={sorter || DEFAULT_SORTER}
          sortDirection={dir}
          renderHead={true}
          headProps={{ sticky: true }}
          onHeadClick={(c, d) => setSort([SORTERS[c.key] || DEFAULT_SORTER, d])}
          chunkSize={30}
          rootMargin="300px"
          rowHeight={ROW_HEIGHTS.dense}
          otherProps={undefined}
        />
      )}
      <CreateNewOrgDialog open={newOrgDialog} onClose={closeNewOrgDialog} />
    </Section>
  );
};
